import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Flame, Plus, Trash2, Pencil, X, Star, Link2, Search } from "lucide-react";

const SECTIONS = [
  { key: "echo", label: "Les Échos" },
  { key: "podcast", label: "Podcasts" },
  { key: "article", label: "Articles" },
  { key: "rencontre", label: "Rencontres" },
  { key: "reportage", label: "Reportages" },
  { key: "culture", label: "Culture & traditions" },
  { key: "media", label: "Médias & créateurs" },
];
const SECTION_LABEL = Object.fromEntries(SECTIONS.map((s) => [s.key, s.label]));
const LINK_TYPES = [
  { key: "event", label: "Événement" },
  { key: "artist", label: "Artiste" },
  { key: "professional", label: "Artisan / Pro" },
];
const LINK_LABEL = Object.fromEntries(LINK_TYPES.map((l) => [l.key, l.label]));

const EMPTY = {
  title: "", section: "article", excerpt: "", body: "", cover: "", author: "swiss-pagan.ch",
  read_minutes: 0, external_url: "", partner: false, featured: false, tags: "", links: [],
};

const input = "w-full bg-[#101216] border border-[#272A34] rounded-md px-3 py-2 text-sm text-stone-200 focus:border-amber-500/60 outline-none";
const label = "block font-mono text-[11px] uppercase tracking-wider text-stone-500 mb-1";

const LinkPicker = ({ links, onChange }) => {
  const [type, setType] = useState("event");
  const [q, setQ] = useState("");
  const { data: options = [] } = useQuery({
    queryKey: ["campfireLinkOptions", type, q],
    queryFn: async () => (await api.get("/admin/campfire/link-options", { params: { type, q } })).data,
  });
  const has = (t, s) => links.some((l) => l.type === t && l.slug === s);
  const add = (opt) => {
    if (links.length >= 3) return toast.error("3 fiches liées maximum");
    if (has(type, opt.slug)) return;
    onChange([...links, { type, slug: opt.slug, label: opt.label }]);
  };
  const remove = (i) => onChange(links.filter((_, idx) => idx !== i));

  return (
    <div className="rounded-md border border-[#272A34] bg-[#101216] p-3">
      <div className="flex items-center gap-2 mb-2">
        <Link2 className="w-4 h-4 text-amber-500" />
        <span className="font-mono text-[11px] uppercase tracking-wider text-stone-400">Fiches liées ({links.length}/3)</span>
      </div>
      {links.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="cm-links-chips">
          {links.map((l, i) => (
            <span key={`${l.type}-${l.slug}`} data-testid={`cm-link-chip-${l.type}-${l.slug}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-950/40 border border-amber-800/40 text-amber-200 text-xs px-2.5 py-1">
              <span className="opacity-70">{LINK_LABEL[l.type]}:</span> {l.label || l.slug}
              <button onClick={() => remove(i)} data-testid={`cm-link-remove-${l.slug}`} className="hover:text-amber-100"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2 mb-2">
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="cm-link-type" className={input + " max-w-[160px]"}>
          {LINK_TYPES.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
        </select>
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-stone-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" data-testid="cm-link-search" className={input + " pl-8"} />
        </div>
      </div>
      <div className="max-h-40 overflow-auto space-y-1">
        {options.length === 0 ? (
          <div className="text-xs text-stone-500 py-2 text-center">Aucun résultat</div>
        ) : options.map((opt) => (
          <button key={opt.slug} onClick={() => add(opt)} disabled={has(type, opt.slug) || links.length >= 3}
            data-testid={`cm-link-option-${opt.slug}`}
            className="w-full flex items-center justify-between gap-2 text-left rounded px-2.5 py-1.5 text-sm text-stone-300 hover:bg-[#181A20] disabled:opacity-40">
            <span className="truncate">{opt.label}{opt.hint ? <span className="text-stone-500"> · {opt.hint}</span> : ""}</span>
            {!has(type, opt.slug) && <Plus className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
          </button>
        ))}
      </div>
    </div>
  );
};

const Form = ({ initial, editingSlug, onDone }) => {
  const qc = useQueryClient();
  const [f, setF] = useState(initial);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        title: f.title, section: f.section, excerpt: f.excerpt, body: f.body, cover: f.cover,
        author: f.author, read_minutes: Number(f.read_minutes) || 0, external_url: f.external_url,
        partner: !!f.partner, featured: !!f.featured,
        tags: (f.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
        links: f.links.map((l) => ({ type: l.type, slug: l.slug })),
      };
      return editingSlug
        ? (await api.put(`/admin/articles/${editingSlug}`, payload)).data
        : (await api.post("/admin/articles", payload)).data;
    },
    onSuccess: () => {
      toast.success(editingSlug ? "Contenu mis à jour" : "Contenu créé");
      qc.invalidateQueries({ queryKey: ["adminArticles"] });
      qc.invalidateQueries({ queryKey: ["campfire"] });
      onDone();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Erreur"),
  });

  return (
    <div className="bg-[#121418] border border-amber-800/40 rounded-lg p-4 space-y-3" data-testid="cm-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className={label}>Titre *</label>
          <input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="cm-title" className={input} />
        </div>
        <div>
          <label className={label}>Rubrique</label>
          <select value={f.section} onChange={(e) => set("section", e.target.value)} data-testid="cm-section" className={input}>
            {SECTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Auteur</label>
          <input value={f.author} onChange={(e) => set("author", e.target.value)} data-testid="cm-author" className={input} />
        </div>
        <div className="sm:col-span-2">
          <label className={label}>Chapô / extrait</label>
          <textarea value={f.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} data-testid="cm-excerpt" className={input} />
        </div>
        <div className="sm:col-span-2">
          <label className={label}>Corps (markdown léger : ## titre, **gras**)</label>
          <textarea value={f.body} onChange={(e) => set("body", e.target.value)} rows={6} data-testid="cm-body" className={input} />
        </div>
        <div className="sm:col-span-2">
          <label className={label}>Image de couverture (URL)</label>
          <input value={f.cover} onChange={(e) => set("cover", e.target.value)} placeholder="https://…" data-testid="cm-cover" className={input} />
        </div>
        <div>
          <label className={label}>Temps de lecture (min)</label>
          <input type="number" value={f.read_minutes} onChange={(e) => set("read_minutes", e.target.value)} data-testid="cm-read" className={input} />
        </div>
        <div>
          <label className={label}>Tags (séparés par des virgules)</label>
          <input value={f.tags} onChange={(e) => set("tags", e.target.value)} data-testid="cm-tags" className={input} />
        </div>
        {(f.section === "podcast" || f.section === "media") && (
          <div className="sm:col-span-2">
            <label className={label}>Lien d'écoute / externe (Spotify, Apple…)</label>
            <input value={f.external_url} onChange={(e) => set("external_url", e.target.value)} placeholder="https://…" data-testid="cm-external" className={input} />
          </div>
        )}
      </div>

      <LinkPicker links={f.links} onChange={(v) => set("links", v)} />

      <div className="flex flex-wrap items-center gap-4">
        <label className="inline-flex items-center gap-2 text-sm text-stone-300"><input type="checkbox" checked={f.partner} onChange={(e) => set("partner", e.target.checked)} data-testid="cm-partner" /> Partenaire</label>
        <label className="inline-flex items-center gap-2 text-sm text-stone-300"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="cm-featured" /> <Star className="w-3.5 h-3.5 text-amber-400" /> À la une</label>
      </div>

      <div className="flex gap-2 pt-1">
        <button onClick={() => save.mutate()} disabled={!f.title.trim() || save.isPending} data-testid="cm-save"
          className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-stone-950 font-semibold px-4 py-2 rounded-md text-sm">
          {save.isPending ? "…" : editingSlug ? "Enregistrer" : "Créer"}
        </button>
        <button onClick={onDone} data-testid="cm-cancel" className="px-4 py-2 rounded-md text-sm bg-[#181A20] border border-[#272A34] text-stone-300 hover:text-stone-100">Annuler</button>
      </div>
    </div>
  );
};

export const CampfireManager = () => {
  const qc = useQueryClient();
  const [mode, setMode] = useState(null); // null | "new" | slug
  const { data: articles = [], isLoading } = useQuery({
    queryKey: ["adminArticles"],
    queryFn: async () => (await api.get("/admin/articles")).data,
  });

  const del = useMutation({
    mutationFn: async (slug) => (await api.delete(`/admin/articles/${slug}`)).data,
    onSuccess: () => {
      toast.success("Contenu supprimé");
      qc.invalidateQueries({ queryKey: ["adminArticles"] });
      qc.invalidateQueries({ queryKey: ["campfire"] });
    },
    onError: () => toast.error("Suppression impossible"),
  });

  const toForm = (a) => ({
    title: a.title || "", section: a.section || "article", excerpt: a.excerpt || "", body: a.body || "",
    cover: a.cover || "", author: a.author || "swiss-pagan.ch", read_minutes: a.read_minutes || 0,
    external_url: a.external_url || "", partner: !!a.partner, featured: !!a.featured,
    tags: (a.tags || []).join(", "),
    links: (a.links || []).map((l) => ({ type: l.type, slug: l.slug, label: l.slug })),
  });

  return (
    <div data-testid="campfire-manager">
      <div className="flex items-center justify-between gap-3 mt-14 mb-2 flex-wrap">
        <h2 className="font-serif text-2xl text-stone-100 flex items-center gap-2">
          <Flame className="w-5 h-5 text-amber-500" /> Campfire — contenus ({articles.length})
        </h2>
        {mode === null && (
          <button onClick={() => setMode("new")} data-testid="cm-new"
            className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-4 py-2 rounded-md text-sm">
            <Plus className="w-4 h-4" /> Nouveau contenu
          </button>
        )}
      </div>
      <p className="text-sm text-stone-400 mb-5 max-w-2xl">
        Créez des Échos, podcasts, articles, rencontres… et reliez-les à des événements, artistes ou artisans (3 max).
      </p>

      {mode === "new" && <div className="mb-6"><Form initial={EMPTY} editingSlug={null} onDone={() => setMode(null)} /></div>}

      {isLoading ? (
        <div className="py-10 text-center text-stone-500" data-testid="cm-loading">Chargement…</div>
      ) : articles.length === 0 && mode === null ? (
        <div className="py-14 text-center text-stone-400 border border-dashed border-[#272A34] rounded-lg" data-testid="cm-empty">
          Aucun contenu Campfire pour l'instant.
        </div>
      ) : (
        <div className="space-y-2">
          {articles.map((a) => (
            <div key={a.slug} data-testid={`cm-row-${a.slug}`}>
              <div className="flex items-center gap-3 rounded-md p-2.5 border border-[#272A34] bg-[#121418]">
                {a.cover ? <img src={a.cover} alt="" className="w-14 h-10 rounded object-cover shrink-0" /> : <div className="w-14 h-10 rounded bg-[#101216] shrink-0 flex items-center justify-center"><Flame className="w-4 h-4 text-stone-600" /></div>}
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-stone-200 truncate flex items-center gap-2">
                    {a.title}
                    {a.featured && <span className="text-[9px] font-mono uppercase text-amber-400 border border-amber-800/50 rounded px-1 py-0.5 flex items-center gap-0.5"><Star className="w-2.5 h-2.5" /> une</span>}
                    {a.partner && <span className="text-[9px] font-mono uppercase text-stone-400 border border-[#272A34] rounded px-1 py-0.5">partenaire</span>}
                  </div>
                  <div className="text-xs text-stone-500 truncate">
                    {SECTION_LABEL[a.section] || a.section}{a.links?.length ? ` · ${a.links.length} fiche(s) liée(s)` : ""}
                  </div>
                </div>
                <a href={`/campfire/${a.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md bg-[#101216] border border-[#272A34] text-stone-400 hover:text-stone-200 shrink-0 text-xs" title="Voir">Voir</a>
                <button onClick={() => setMode(mode === a.slug ? null : a.slug)} data-testid={`cm-edit-${a.slug}`}
                  className="p-1.5 rounded-md bg-[#101216] border border-[#272A34] text-stone-300 hover:text-amber-300 shrink-0" title="Modifier"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => window.confirm("Supprimer ce contenu ?") && del.mutate(a.slug)} data-testid={`cm-delete-${a.slug}`}
                  className="p-1.5 rounded-md bg-red-950/40 border border-red-800/40 text-red-300 hover:bg-red-900/40 shrink-0" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
              </div>
              {mode === a.slug && <div className="mt-2 mb-4"><Form initial={toForm(a)} editingSlug={a.slug} onDone={() => setMode(null)} /></div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CampfireManager;
